import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { catchError, map, Observable, of, share, tap } from 'rxjs';
import { ConfigService } from 'src/app/common/services/config/config.service';
import {
  IAppState,
  IFrontendSettings,
} from '../ngrx.stores/frontend.settings/states';
import { frontendSettingsSelector } from '../ngrx.stores/frontend.settings/selectors';
import { ApiErrorHandler } from 'src/app/common/services/api/api.error.handler';

@Injectable({
  providedIn: 'root',
})
export class GoogleMapService {
  apiLoaded$: Observable<boolean>;
  isLoaded = false;
  placesLibrary: google.maps.PlacesLibrary;
  ngRxStore = inject(Store<IAppState>);
  apiErrorHandler = inject(ApiErrorHandler);
  frontendSettings: IFrontendSettings;
  private apiLoadPromise: Promise<boolean>;

  constructor(public httpClient: HttpClient) {
    this.ngRxStore
      .pipe(select(frontendSettingsSelector))
      .subscribe((settings) => {
        settings && (this.frontendSettings = settings);
      });
  }

  loadGoogleMapApi(): Observable<boolean> {
    if (this.isLoaded) return of(true);
    if (this.apiLoaded$) return this.apiLoaded$;

    if (!this.frontendSettings?.customSettings?.dataSource?.googleKey) {
      this.apiErrorHandler.showError('Google app key not found!');
      return of(false);
    }

    this.apiLoaded$ = this.httpClient
      .jsonp(
        `https://maps.googleapis.com/maps/api/js?v=3&libraries=places&language=en&key=${this.frontendSettings.customSettings.dataSource.googleKey}`,
        'callback'
      )
      .pipe(
        map(() => true),
        share(),
        tap(() => {
          if (window['google']?.maps?.places) {
            console.log('Places library is available');
            this.isLoaded = true;
            this.placesLibrary = google.maps.places;
          } else {
            console.error('google.maps.places not available');
          }
        }),
        catchError((e) => {
          console.error('Loading Google Map Script Error', e);
          return of(false);
        })
      );

    return this.apiLoaded$;
  }

  // Method to get the places library
  getPlacesLibrary(): google.maps.PlacesLibrary {
    return this.placesLibrary;
  }

  // Fixed method binding and parameter types
  getDistanceFromLatLng(lat1: number, lon1: number, lat2: number, lon2: number): number {
    if (!lat1 || !lon1 || !lat2 || !lon2) {
      return 0;
    }

    const R = 6371; // Radius of the earth in km
    const dLat = this.deg2rad(lat2 - lat1);
    const dLon = this.deg2rad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.deg2rad(lat1)) *
        Math.cos(this.deg2rad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const d = R * c; // Distance in km
    return d * 1000; // Convert to meters
  }

  private deg2rad(deg: number): number {
    return deg * (Math.PI / 180);
  }

  getDirection(
    attractions: any[],
    travelMode: TravelMode,
    callback: (response: google.maps.DirectionsRoute) => void,
    errorCallback?: (error: string) => void
  ): void {
    if (!attractions || !attractions.length || attractions.length === 1) {
      const errorMsg = 'Invalid attractions data - need at least 2 locations';
      return errorCallback ? errorCallback(errorMsg) : console.error(errorMsg);
    }

    // Ensure Google Maps API is loaded
    if (!window['google']?.maps) {
      const errorMsg = 'Google Maps API not loaded';
      return errorCallback ? errorCallback(errorMsg) : console.error(errorMsg);
    }

    let origin: google.maps.LatLng | null = null;
    let destination: google.maps.LatLng | null = null;
    const waypoints: google.maps.DirectionsWaypoint[] = [];

    attractions.forEach((attraction, index) => {
      const lat = attraction.item?.address?.lat || attraction.item?.lat;
      const lng = attraction.item?.address?.lng || attraction.item?.lng;
      
      if (!lat || !lng) {
        console.warn(`Invalid coordinates for attraction at index ${index}`);
        return;
      }

      const latlng = new google.maps.LatLng(lat, lng);
      
      if (index === 0) {
        origin = latlng;
      } else if (index === attractions.length - 1) {
        destination = latlng;
      } else {
        waypoints.push({ location: latlng, stopover: true });
      }
    });

    if (!origin || !destination) {
      const errorMsg = 'Invalid origin or destination coordinates';
      return errorCallback ? errorCallback(errorMsg) : console.error(errorMsg);
    }

    const directionsService = new google.maps.DirectionsService();

    directionsService.route(
      {
        origin: origin,
        destination: destination,
        waypoints: waypoints,
        optimizeWaypoints: false,
        travelMode: google.maps.TravelMode[travelMode],
      },
      (response, status) => {
        if (status === google.maps.DirectionsStatus.OK && response?.routes?.[0]) {
          callback(response.routes[0]);
        } else {
          const errorMsg = `Directions service failed with status: ${status}`;
          console.error('Direction Service error', response, status);
          if (errorCallback) {
            errorCallback(errorMsg);
          }
        }
      }
    );
  }

  // Helper method to check if API is ready
  isApiReady(): boolean {
    return this.isLoaded && !!window['google']?.maps;
  }

  // Method to wait for API to be ready
  waitForApiReady(): Promise<boolean> {
    if (this.isApiReady()) {
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (this.isApiReady()) {
          clearInterval(checkInterval);
          resolve(true);
        }
      }, 100);

      // Timeout after 10 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        resolve(false);
      }, 10000);
    });
  }
}

export enum TravelMode {
  DRIVING = 'DRIVING',
  BICYCLING = 'BICYCLING',
  WALKING = 'WALKING',
  TRANSIT = 'TRANSIT',
}